import { v4 } from '@rotra-air-ocean/rotranext-shared-code';

import { ViewEdge, ViewGraphTree, ViewNode } from '../components/types.js';

function edge(
  source: string,
  target: string,
  label: string,
  kind: 'then' | 'changes',
): ViewEdge {
  return { id: `${source}->${target}:${label}`, source, target, label, kind };
}

function edgesFor(id: string, step: v4.StepSpec): ViewEdge[] {
  switch (step.kind) {
    case 'auto':
    case 'apiCall':
    case 'outlookCategory':
    case 'llm':
    case 'systemOne': {
      return step.next ? [edge(id, step.next, 'then', 'then')] : [];
    }
    case 'userInput': {
      return step.next ? [edge(id, step.next, 'reply', 'then')] : [];
    }
    case 'decision': {
      const out: ViewEdge[] = [];
      for (const action of step.actions) {
        out.push(edge(id, action.next, action.label, 'then'));
      }
      return out;
    }
    case 'branch': {
      const out: ViewEdge[] = [];
      for (const [key, target] of Object.entries(step.cases ?? {})) {
        out.push(edge(id, target, key, 'then'));
      }
      if (step.default) {
        out.push(edge(id, step.default, 'default', 'then'));
      }
      return out;
    }
    case 'subWorkflow': {
      return step.onDone ? [edge(id, step.onDone, 'done', 'then')] : [];
    }
    case 'final': {
      return [];
    }
  }
}

/**
 * Build a single-level `ViewGraphTree` from a spec for the editor preview.
 * Sub-workflow nodes are not drillable here (the referenced spec isn't loaded);
 * `selectedStepId` is highlighted so the preview tracks the editor selection.
 */
export function specToGraph(
  workflowId: string,
  title: string,
  spec: v4.WorkflowSpec,
  selectedStepId?: string | null,
): ViewGraphTree {
  const nodes: ViewNode[] = Object.entries(spec.steps).map(([id, s]) => ({
    id,
    label: s.label || id,
    kind: s.kind,
    active: id === selectedStepId,
  }));
  const edges: ViewEdge[] = Object.entries(spec.steps).flatMap(([id, s]) =>
    edgesFor(id, s),
  );
  return {
    root: workflowId,
    workflows: {
      [workflowId]: {
        workflowId,
        title,
        nodes,
        edges,
        active: false,
      },
    },
    activePath: [],
  };
}
